/** Pure helpers for the cheque camera scanner (kept DOM-free so they are unit-testable). */

export type CameraFacing = 'environment' | 'user';

export type CameraSupport = 'ok' | 'insecure_context' | 'unsupported';

interface CameraEnv {
  readonly isSecureContext?: boolean | undefined;
  readonly mediaDevices?: { readonly getUserMedia?: unknown } | undefined;
}

export function cameraSupport(env: CameraEnv): CameraSupport {
  if (env.isSecureContext === false) return 'insecure_context';
  if (typeof env.mediaDevices?.getUserMedia !== 'function') return 'unsupported';
  return 'ok';
}

export function cameraErrorMessage(err: unknown, support: CameraSupport = 'ok'): string {
  if (support === 'insecure_context') {
    return 'دوربین فقط در اتصال امن (HTTPS) در دسترس است. از «انتخاب از گالری / فایل» استفاده کنید.';
  }
  if (support === 'unsupported') {
    return 'مرورگر شما از دسترسی به دوربین پشتیبانی نمی‌کند. از «انتخاب از گالری / فایل» استفاده کنید.';
  }
  const name = err instanceof Error ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'دسترسی به دوربین رد شده است. در تنظیمات مرورگر اجازهٔ دوربین را فعال کنید یا فایل بارگذاری کنید.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'دوربینی روی این دستگاه پیدا نشد. از «انتخاب از گالری / فایل» استفاده کنید.';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'دوربین توسط برنامهٔ دیگری در حال استفاده است. آن را ببندید و دوباره تلاش کنید.';
    default:
      return 'اتصال به دوربین ناموفق بود. دوباره تلاش کنید یا فایل بارگذاری کنید.';
  }
}

/** Preferred constraints first, progressively relaxed so odd devices still connect. */
export function cameraConstraintAttempts(facing: CameraFacing): MediaStreamConstraints[] {
  return [
    {
      video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: false,
    },
    { video: { facingMode: { ideal: facing } }, audio: false },
    { video: true, audio: false },
  ];
}

export function isImageDataUrl(value: string): boolean {
  return /^data:image\/(png|jpe?g|webp|heic|heif);base64,/i.test(value);
}

export const MAX_CHEQUE_IMAGE_BYTES = 8 * 1024 * 1024;

export function validateChequeImageFile(file: {
  readonly type: string;
  readonly size: number;
}): string | null {
  if (!file.type.startsWith('image/')) return 'فقط فایل تصویری (JPG/PNG) پذیرفته می‌شود.';
  if (file.size > MAX_CHEQUE_IMAGE_BYTES) return 'حجم تصویر نباید بیشتر از ۸ مگابایت باشد.';
  return null;
}
